import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { Course, CourseSectionRepoItem, CourseTeacher } from '@/types/api/course.types';
import { SDG_LABELS, sdgIconSrc } from '@/types';
import {
  BarChart3,
  ChevronsRight,
  ClipboardList,
  EllipsisVertical,
  FileText,
  GraduationCap,
  MessageSquare,
  Paperclip,
  Play,
  PlayCircle,
  Plus,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { InstructorsTabContent } from './instructors-tab';
import { getVideoEmbedUrl, getVideoThumbnailUrl } from './video-utils';
import UsersAttendanceContent from './users-attendance-content';
import { EditableRichInfoCard } from './editable-info-fields';
import CourseAssignmentsContent from './course-assignments-content';
import { AddSdgsDialog } from '@/components/sdgs/add-sdgs-dialog';
import CourseFeedbacksContent from './course-feedbacks-content';
import CourseStudentAnalyticsPanel from './course-student-analytics-panel';
import CourseAssessmentStructureContent from './course-assessment-structure-content';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePatchCourseMutation } from '@/state/course';
import { useAddPublicImageResourceMutation } from '@/state/user/client-api';
import { toast } from 'sonner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface SectionGroup {
  sectionId: number;
  sectionName: string;
  count: number;
}

interface MainSectionsContentProps {
  tenant: string;
  courseId: number;
  currentMainSection: string;
  course?: Course;
  boardId?: number | null;
  boardName?: string | null;
  canEdit: boolean;
  isLearner: boolean;
  currentUserId?: string | null;
  courseTeachers?: CourseTeacher[];
  onOpenAddInstructor: () => void;
  onToggleInstructorActive: (teacherMappingId: number, active: boolean) => void;
  isPatchingTeacher: boolean;
  onOpenDeleteInstructor: (teacherMappingId: number) => void;
  isDeletingTeacher: boolean;
  courseMainVideos: CourseSectionRepoItem[];
  sectionVideoGroups: SectionGroup[];
  onNavigateToSectionVideos: (sectionId: number) => void;
  onOpenAddCourseVideoModal: () => void;
  onOpenEditVideoModal: (video: CourseSectionRepoItem) => void;
  onSetVideoToDelete: (video: CourseSectionRepoItem) => void;
  isPatchingLmsRepo: boolean;
  isDeletingLmsRepo: boolean;
  playingVideoId: number | null;
  onSetPlayingVideoId: (id: number) => void;
  courseMainDocuments: CourseSectionRepoItem[];
  sectionAttachmentGroups: SectionGroup[];
  onNavigateToSectionAttachments: (sectionId: number) => void;
  onNavigateToSectionAssignments: (sectionId: number) => void;
  courseFileInputRef: React.RefObject<HTMLInputElement | null>;
  onUploadCourseFile: (file: File) => void;
  onSetDocumentToDelete: (doc: CourseSectionRepoItem) => void;
}

const renderEmptyState = (title: string, description: string) => (
  <div className="h-full min-h-[360px] rounded-xl border border-dashed bg-card p-6 shadow-sm">
    <div className="flex h-full items-center justify-center">
      <div className="max-w-md text-center">
        <p className="text-base font-semibold text-card-foreground">{title}</p>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  </div>
);

const MainSectionsContent = ({
  tenant,
  courseId,
  currentMainSection,
  course,
  boardId,
  boardName,
  canEdit,
  isLearner,
  currentUserId,
  courseTeachers,
  onOpenAddInstructor,
  onToggleInstructorActive,
  isPatchingTeacher,
  onOpenDeleteInstructor,
  isDeletingTeacher,
  courseMainVideos,
  sectionVideoGroups,
  onNavigateToSectionVideos,
  onOpenAddCourseVideoModal,
  onOpenEditVideoModal,
  onSetVideoToDelete,
  isPatchingLmsRepo,
  isDeletingLmsRepo,
  playingVideoId,
  onSetPlayingVideoId,
  courseMainDocuments,
  sectionAttachmentGroups,
  onNavigateToSectionAttachments,
  onNavigateToSectionAssignments,
  courseFileInputRef,
  onUploadCourseFile,
  onSetDocumentToDelete,
}: MainSectionsContentProps) => {
  const [patchCourse] = usePatchCourseMutation();
  const [addPublicImageResource] = useAddPublicImageResourceMutation();
  const [activeSdgPreview, setActiveSdgPreview] = useState<number | null>(null);
  const sdgContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeSdgPreview === null) return;
    const handleOutsidePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (sdgContainerRef.current?.contains(target)) return;
      setActiveSdgPreview(null);
    };
    document.addEventListener('mousedown', handleOutsidePointerDown);
    document.addEventListener('touchstart', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
      document.removeEventListener('touchstart', handleOutsidePointerDown);
    };
  }, [activeSdgPreview]);

  const handleUploadFile = useCallback(
    async (file: File): Promise<string> => {
      try {
        const formData = new FormData();
        formData.append('image_path', file);
        const data = await addPublicImageResource({ data: formData }).unwrap();
        const src = data?.image_path || data?.url || data?.file;
        if (src) return src;
        throw new Error('No image path returned from upload');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to upload image.');
        return URL.createObjectURL(file);
      }
    },
    [addPublicImageResource]
  );

  const patchCourseRichField = useCallback(
    async (fieldName: 'course_overview' | 'learning_objectives', value: string) => {
      const formData = new FormData();
      formData.append(fieldName, value);
      await patchCourse({
        tenant,
        courseId,
        data: formData,
      }).unwrap();
      toast.success('Updated successfully');
    },
    [courseId, patchCourse, tenant]
  );

  const courseSdgs = useMemo(
    () =>
      (course?.sdgs ?? [])
        .map((raw) => (typeof raw === 'string' ? Number(raw) : raw))
        .filter((goal): goal is number => Number.isFinite(goal)),
    [course?.sdgs]
  );
  const hasCourseBanner = Boolean(course?.course_banner);

  const handleAddCourseSDGs = useCallback(
    async (selected: number[]) => {
      if (!selected.length) return;
      const nextSdgs = Array.from(new Set([...courseSdgs, ...selected]));
      try {
        await patchCourse({
          tenant,
          courseId,
          data: {
            sdgs: nextSdgs,
          },
        }).unwrap();
        toast.success(`Added ${selected.length} SDG(s).`);
      } catch {
        toast.error('Failed to update SDGs.');
      }
    },
    [courseSdgs, courseId, patchCourse, tenant]
  );

  const handleRemoveCourseSDG = useCallback(
    async (goalToRemove: number) => {
      const nextSdgs = courseSdgs.filter((goal) => goal !== goalToRemove);
      try {
        await patchCourse({
          tenant,
          courseId,
          data: {
            sdgs: nextSdgs,
          },
        }).unwrap();
        toast.success('SDG removed.');
      } catch {
        toast.error('Failed to update SDGs.');
      }
    },
    [courseSdgs, courseId, patchCourse, tenant]
  );

  if (currentMainSection === 'course-details') {
    return (
      <>
        <section
          className={`relative rounded-xl border overflow-visible ${hasCourseBanner ? '' : 'bg-gradient-to-r from-sky-900 via-slate-800 to-cyan-800'}`}
        >
          {hasCourseBanner ? (
            <>
              <div className="absolute inset-0 overflow-hidden rounded-xl">
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${course?.course_banner}')` }}
                />
              </div>
              <div className="absolute inset-0 rounded-xl bg-black/35" />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-card via-card/70 to-transparent" />
              <div className="absolute inset-0 rounded-xl bg-muted/10 backdrop-blur-[2px]" />
            </>
          ) : (
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.18),transparent_35%),radial-gradient(circle_at_80%_70%,rgba(34,211,238,0.2),transparent_40%)]" />
          )}

          <div className="relative p-6">
            <div className="relative">
              <p className="text-3xl text-white/95 tracking-wide">Course details</p>
              <p className="mt-1 text-sm text-white/70">
                Structured view of outcomes, teaching team, and assessment.
              </p>
            </div>
            {canEdit ? (
              <div className="relative mt-4">
                <AddSdgsDialog
                  onChangeMultiple={handleAddCourseSDGs}
                  existingSDGs={courseSdgs}
                />
              </div>
            ) : null}
            {courseSdgs.length > 0 ? (
              <div ref={sdgContainerRef} className="relative mt-4 flex flex-wrap items-center gap-2">
                {courseSdgs.map((goal) => (
                  <div
                    key={goal}
                    className={`group relative flex items-center gap-2 rounded-md border border-white/20 bg-black/20 px-2.5 py-1.5 backdrop-blur-sm transition-transform ${
                      activeSdgPreview === goal ? 'z-30' : 'z-10 hover:z-30'
                    }`}
                    title={`SDG ${goal}: ${SDG_LABELS[goal]}`}
                  >
                    <img
                      src={sdgIconSrc(goal) || '/placeholder.svg'}
                      alt={`SDG ${goal}`}
                      className={`h-8 w-8 transition-transform duration-200 ${
                        activeSdgPreview === goal ? 'scale-[5]' : ''
                      }`}
                      loading="lazy"
                      onMouseEnter={() => {
                        setActiveSdgPreview(goal);
                      }}
                      onMouseLeave={() => {
                        setActiveSdgPreview((prev) => (prev === goal ? null : prev));
                      }}
                      onClick={() => {
                        setActiveSdgPreview((prev) => (prev === goal ? null : goal));
                      }}
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleRemoveCourseSDG(goal);
                        }}
                        className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-white/80 transition-all duration-200 hover:bg-white/15 hover:text-white"
                        aria-label={`Remove SDG ${goal}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="relative mt-4 text-sm italic text-white/70">No SDGs selected yet.</p>
            )}
          </div>
        </section>

        <Tabs defaultValue="course-info" className="w-full min-h-[60vh] flex flex-col">
          <TabsList className="h-auto w-full lg:w-auto shrink-0 justify-start overflow-x-auto gap-2 rounded-xl border bg-card p-2 shadow-sm">
            <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="course-info">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Course info</span>
            </TabsTrigger>
            <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="instructors">
              <Users className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Instructors</span>
            </TabsTrigger>
            <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="assessment">
              <ClipboardList className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Course assessment structure</span>
            </TabsTrigger>
            <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="feedbacks">
              <MessageSquare className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Course feedbacks</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="course-info" className="mt-3 flex-1">
            <div className="space-y-4">
              <EditableRichInfoCard
                title="Course overview"
                subtitle="High-level summary of this course"
                icon={<FileText className="h-4 w-4" />}
                value={course?.course_overview || course?.course_brief}
                placeholder="Add a course overview..."
                canEdit={canEdit}
                onUploadFile={handleUploadFile}
                onSave={async (nextValue) => {
                  await patchCourseRichField('course_overview', nextValue);
                }}
              />
              <EditableRichInfoCard
                title="Learning objectives"
                subtitle="Core goals students should achieve"
                icon={<GraduationCap className="h-4 w-4" />}
                value={course?.learning_objectives}
                placeholder="Add learning objectives..."
                canEdit={canEdit}
                onUploadFile={handleUploadFile}
                onSave={async (nextValue) => {
                  await patchCourseRichField('learning_objectives', nextValue);
                }}
              />
            </div>
          </TabsContent>

          <TabsContent value="instructors" className="mt-3 flex-1">
            <div className="h-full min-h-[420px]">
              <InstructorsTabContent
                course={course}
                courseTeachers={courseTeachers}
                canEdit={canEdit}
                onOpenAddInstructor={onOpenAddInstructor}
                onToggleInstructorActive={onToggleInstructorActive}
                isPatchingTeacher={isPatchingTeacher}
                onOpenDeleteInstructor={onOpenDeleteInstructor}
                isDeletingTeacher={isDeletingTeacher}
              />
            </div>
          </TabsContent>

          <TabsContent value="assessment" className="mt-3 flex-1">
            <CourseAssessmentStructureContent
              tenant={tenant}
              courseId={courseId}
              canEdit={canEdit}
            />
          </TabsContent>

          <TabsContent value="feedbacks" className="mt-3 flex-1">
            <CourseFeedbacksContent
              tenant={tenant}
              courseId={courseId}
              canEdit={canEdit}
              isLearner={isLearner}
            />
          </TabsContent>
        </Tabs>
      </>
    );
  }

  if (currentMainSection === 'videos') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => onSetPlayingVideoId(-1)}
            className="group relative overflow-hidden rounded-2xl border border-cyan-300/40 bg-gradient-to-br from-cyan-100/60 via-sky-100/70 to-indigo-100/80 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-cyan-800/50 dark:from-cyan-950/40 dark:via-sky-950/40 dark:to-indigo-950/40"
          >
            <div className="absolute right-4 top-4 rounded-full bg-background/80 px-2 py-0.5 text-xs font-medium text-muted-foreground">
              Main
            </div>
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 text-sky-700 ring-1 ring-sky-300/60 dark:bg-sky-900/70 dark:text-sky-100 dark:ring-sky-700/60">
              <PlayCircle className="h-5 w-5" />
            </div>
            <p className="mt-6 text-lg font-semibold text-foreground">Course videos</p>
            <p className="mt-1 text-sm text-muted-foreground">{courseMainVideos.length} videos</p>
            <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-sky-700 dark:text-sky-300">
              View in this page
              <ChevronsRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </p>
          </button>
          {sectionVideoGroups.map((group) => (
            <button
              key={group.sectionId}
              type="button"
              onClick={() => onNavigateToSectionVideos(group.sectionId)}
              className="group relative overflow-hidden rounded-2xl border border-violet-300/40 bg-gradient-to-br from-violet-100/65 via-fuchsia-100/75 to-blue-100/80 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-violet-800/50 dark:from-violet-950/45 dark:via-fuchsia-950/35 dark:to-blue-950/35"
            >
              <div className="absolute right-4 top-4 rounded-full bg-background/80 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Chapter
              </div>
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 text-violet-700 ring-1 ring-violet-300/60 dark:bg-violet-900/70 dark:text-violet-100 dark:ring-violet-700/60">
                <PlayCircle className="h-5 w-5" />
              </div>
              <p className="mt-6 text-lg font-semibold text-foreground line-clamp-1">
                {group.sectionName}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{group.count} videos</p>
              <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-violet-700 dark:text-violet-300">
                Open chapter videos
                <ChevronsRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </p>
            </button>
          ))}
        </div>

        <div className="h-full min-h-[420px] rounded-xl border bg-card p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {canEdit && (
              <button
                type="button"
                onClick={onOpenAddCourseVideoModal}
                className="min-h-[160px] rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/40 bg-background/70 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground"
              >
                <Plus className="h-5 w-5" />
                <span className="font-medium">Add video link</span>
              </button>
            )}
            {courseMainVideos.length === 0 && (
              <div className="md:col-span-2 xl:col-span-3">
                {renderEmptyState(
                  'No course-level videos',
                  'Course-level videos appear here when a video is added without a chapter section.'
                )}
              </div>
            )}
            {courseMainVideos.length > 0 &&
              courseMainVideos.map((video) => {
                const embedUrl = getVideoEmbedUrl(video.content);
                const thumbnailUrl = getVideoThumbnailUrl(video.content);
                return (
                  <div key={video.id} className="rounded-xl border bg-background p-3 shadow-sm">
                    <div className="relative overflow-hidden rounded-lg border bg-muted">
                      {playingVideoId === video.id && embedUrl ? (
                        <iframe
                          src={embedUrl}
                          className="aspect-video w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          title={video.name}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (embedUrl) {
                              onSetPlayingVideoId(video.id);
                            } else {
                              window.open(video.content, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          className="group relative block aspect-video w-full text-left"
                        >
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt={video.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
                          )}
                          <div className="absolute inset-0 bg-black/35 transition-colors group-hover:bg-black/45" />
                          <p className="absolute left-3 top-2 right-12 line-clamp-1 text-white text-lg font-semibold drop-shadow-sm">
                            {video.name}
                          </p>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-xl ring-1 ring-black/10">
                              <Play className="h-7 w-7 fill-current ml-0.5" />
                            </span>
                          </div>
                        </button>
                      )}
                      {canEdit && (
                        <div className="absolute right-2 top-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-background/90 backdrop-blur hover:bg-background"
                              >
                                <EllipsisVertical className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => onOpenEditVideoModal(video)}
                                disabled={isPatchingLmsRepo}
                              >
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={() => onSetVideoToDelete(video)}
                                disabled={isDeletingLmsRepo}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  }

  if (currentMainSection === 'attachments') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <button
            type="button"
            className="group relative overflow-hidden rounded-2xl border border-cyan-300/40 bg-gradient-to-br from-cyan-100/60 via-sky-100/70 to-indigo-100/80 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-cyan-800/50 dark:from-cyan-950/40 dark:via-sky-950/40 dark:to-indigo-950/40"
          >
            <div className="absolute right-4 top-4 rounded-full bg-background/80 px-2 py-0.5 text-xs font-medium text-muted-foreground">
              Main
            </div>
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 text-sky-700 ring-1 ring-sky-300/60 dark:bg-sky-900/70 dark:text-sky-100 dark:ring-sky-700/60">
              <Paperclip className="h-5 w-5" />
            </div>
            <p className="mt-6 text-lg font-semibold text-foreground">Course attachments</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {courseMainDocuments.length} files
            </p>
          </button>
          {sectionAttachmentGroups.map((group) => (
            <button
              key={group.sectionId}
              type="button"
              onClick={() => onNavigateToSectionAttachments(group.sectionId)}
              className="group relative overflow-hidden rounded-2xl border border-violet-300/40 bg-gradient-to-br from-violet-100/65 via-fuchsia-100/75 to-blue-100/80 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-violet-800/50 dark:from-violet-950/45 dark:via-fuchsia-950/35 dark:to-blue-950/35"
            >
              <div className="absolute right-4 top-4 rounded-full bg-background/80 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Chapter
              </div>
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 text-violet-700 ring-1 ring-violet-300/60 dark:bg-violet-900/70 dark:text-violet-100 dark:ring-violet-700/60">
                <Paperclip className="h-5 w-5" />
              </div>
              <p className="mt-6 text-lg font-semibold text-foreground line-clamp-1">
                {group.sectionName}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{group.count} files</p>
              <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-violet-700 dark:text-violet-300">
                Open chapter attachments
                <ChevronsRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </p>
            </button>
          ))}
        </div>

        <div className="h-full min-h-[420px] rounded-xl border bg-card p-6 shadow-sm">
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => courseFileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) onUploadCourseFile(file);
                }}
                className="mb-4 min-h-[120px] w-full rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/40 bg-background/70 transition-colors px-4 py-5 text-center"
              >
                <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Upload className="h-5 w-5" />
                  <p className="font-medium">Add course-level attachment</p>
                  <p className="text-sm">Supported: PDF, PPT, DOC, DOCX, XLSX, PNG, JPEG</p>
                </div>
              </button>
              <input
                ref={courseFileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.ppt,.pptx,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUploadCourseFile(file);
                  e.target.value = '';
                }}
              />
            </>
          )}
          {courseMainDocuments.length === 0 ? (
            renderEmptyState(
              'No course-level attachments',
              'Course-level attachments appear here when a document is added without a chapter section.'
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {courseMainDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="group relative rounded-xl border bg-background shadow-sm overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (!doc.document) return;
                      window.open(doc.document, '_blank', 'noopener,noreferrer');
                    }}
                    className="block w-full text-left"
                  >
                    <div className="aspect-[1.8/1] bg-muted flex items-center justify-center">
                      {doc.document ? (
                        <img
                          src={doc.document}
                          alt={doc.name}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Paperclip className="h-8 w-8 text-muted-foreground/80" />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                    </div>
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onSetDocumentToDelete(doc)}
                      className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-destructive shadow-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      title="Delete document"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (currentMainSection === 'users') {
    return (
      <UsersAttendanceContent
        tenant={tenant}
        courseId={courseId}
        boardId={boardId}
        boardName={boardName}
        canEdit={canEdit}
        mode="users"
        courseGradingStructure={course?.grading_structure ?? null}
      />
    );
  }

  if (currentMainSection === 'analytics') {
    if (!currentUserId) {
      return renderEmptyState('Analytics unavailable', 'Student data is not available.');
    }
    return (
      <div className="h-full min-h-[420px] rounded-xl border bg-card p-4 shadow-sm sm:p-6">
        <div className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
          <BarChart3 className="h-4 w-4" />
          Personal course analytics
        </div>
        <CourseStudentAnalyticsPanel
          tenant={tenant}
          courseId={courseId}
          studentId={currentUserId}
          courseGradingStructure={course?.grading_structure ?? null}
          canEdit={canEdit}
        />
      </div>
    );
  }

  if (currentMainSection === 'attendance') {
    return (
      <UsersAttendanceContent
        tenant={tenant}
        courseId={courseId}
        boardId={boardId}
        boardName={boardName}
        canEdit={canEdit}
        mode="attendance"
        courseGradingStructure={course?.grading_structure ?? null}
      />
    );
  }

  if (currentMainSection === 'assignments') {
    return (
      <CourseAssignmentsContent
        canEdit={canEdit}
        tenant={tenant}
        courseId={courseId}
        courseStructureId={course?.course_structure ?? 0}
        courseSectionId={null}
        onNavigateToSectionAssignments={onNavigateToSectionAssignments}
      />
    );
  }

  return renderEmptyState(
    'Section under construction',
    `${currentMainSection || 'This section'} will be integrated soon.`
  );
};

export default MainSectionsContent;
